import apiClient from "./api-client";
import { handleApiError } from "./error-handler";
import {
  WorkflowTransitionResult,
  ProposalStatusHistory,
  ExecuteWorkflowActionRequest,
  ProposalWorkflowAction,
  CanViewProposalResponse,
  BulkTransmitResult
} from "../models/ProposalWorkflow";

/**
 * Execute a workflow action on a proposal
 */
export const executeWorkflowAction = async (
  request: ExecuteWorkflowActionRequest
): Promise<WorkflowTransitionResult> => {
  try {
    const response = await apiClient.post(
      `/freelancer-proposals/${request.proposalId}/workflow/action`,
      {
        action: request.action,
        reason: request.reason,
        message: request.message,
        freelancerDeclineReason: request.freelancerDeclineReason,
        freelancerDeclineReasonDetails: request.freelancerDeclineReasonDetails,
        clientDeclineReason: request.clientDeclineReason,
        clientDeclineReasonDetails: request.clientDeclineReasonDetails,
        castingProposedSlots: request.castingProposedSlots,
        hasContentChanges: request.hasContentChanges
      }
    );
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error executing workflow action');
  }
};

/**
 * Get available workflow actions for a proposal
 */
export const getAvailableActions = async (
  proposalId: number
): Promise<ProposalWorkflowAction[]> => {
  try {
    const response = await apiClient.get(
      `/freelancer-proposals/${proposalId}/workflow/available-actions`
    );
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error fetching available actions');
  }
};

/**
 * Get status history for a proposal
 */
export const getProposalStatusHistory = async (
  proposalId: number
): Promise<ProposalStatusHistory[]> => {
  try {
    const response = await apiClient.get(
      `/freelancer-proposals/${proposalId}/status-histories`
    );
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error fetching status history');
  }
};

/**
 * Check if current user can view a proposal
 */
export const canViewProposal = async (
  proposalId: number
): Promise<CanViewProposalResponse> => {
  try {
    const response = await apiClient.get(
      `/freelancer-proposals/${proposalId}/permissions/can-view`
    );
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error checking view permission');
  }
};

/**
 * Check if current user can edit a proposal
 */
export const canEditProposal = async (
  proposalId: number
): Promise<boolean> => {
  try {
    const response = await apiClient.get(
      `/freelancer-proposals/${proposalId}/permissions/can-edit`
    );
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error checking edit permission');
  }
};

/**
 * Bulk transmit proposals to client
 */
export const bulkTransmitToClient = async (
  proposalIds: number[]
): Promise<BulkTransmitResult> => {
  try {
    const response = await apiClient.post(
      `/freelancer-proposals/bulk-transmit-to-client`,
      { proposalIds }
    );
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error bulk transmitting proposals');
  }
};

/**
 * Get all proposals for a project (for CSM/Admin)
 */
export const getProjectProposals = async (
  projectId: number
): Promise<any[]> => {
  try {
    const response = await apiClient.get(
      `/freelancer-proposals/all`,
      { params: { projectId } }
    );
    return response.data;
  } catch (error: any) {
    throw handleApiError(error, 'Error fetching project proposals');
  }
};